<template>
  <div
    :class="[
      'product-thumbnail-container',
      showChevronOnHorizontalCard ? 'grid-x' : '',
    ]"
  >
    <div
      v-if="product"
      :class="[
        productThumbnailContainerClasses,
        showChevronOnHorizontalCard ? 'cell small-10' : '',
      ]"
    >
      <div :class="['product-image', { 'clickAction': clickAction === 'fireEvent' }]">
        <div
          :id="`product-thumbnail-${product?.id}`"
          ref="imageSize"
        >
          <a
            :tabindex="-1"
            :href="generatedUrl"
            :aria-labelledby="product?.detail?.name"
            :title="product?.detail?.name"
            :target="openInNewTab ? '_blank' : '_self'"
            :rel="openInNewTab ? 'noopener' : null"
            draggable="false"
            @click="getMoreDetails"
          >
            <ProductThumbnailImage
              :imagery="product?.imagery"
              :traits="product?.traits"
              :selected-color="selectedColor"
              :alt="product?.detail?.name"
              :url-template="urlTemplate?.product"
              :image-aspect-ratio="imageAspectRatio"
              :image-sizes-by-breakpoints="imageSizesByBreakpoints"
              :image-width="imageWidth"
              :alignment="alignment"
              :disable-slideshow="disableSlideshow"
              :is-large-card="isLargeCard"
              :image-badge="imageBadge"
              :selected-size-images="selectedSizeImages"
              :in-store-only="product?.storeOnly?.inStoreOnly"
              :skeleton="skeleton"
              :skeleton-options="skeletonOptions"
              :slideshow-props="slideshowParams"
              :retina-adjustment="retinaAdjustment"
              :retina-quality="retinaQuality"
              :draggable="draggable"
              :fetch-priority="fetchPriority"
              :lazy-load="lazyLoad"
              :first-image-priority="firstImagePriority"
              :is-hovering="isHoveringValue"
              :color-swatch="colorSwatchData"
              :first-picture-native-lazy="firstPictureNativeLazy"
              @chevronDirection="getChevronDirection"
            />
          </a>
          <div
            v-if="showWishlist"
            :class="wishListSpacing"
          >
            <WishListIcon
              v-if="product?.id"
              :id="product?.id"
              @wishListIconClicked="wishListIconClicked"
            />
          </div>
        </div>

        <div
          v-if="!isBcom && showCornerBadge && isSponsoredLabel"
          class="corner-badge-container is-sponsored-label"
        >
          <div
            :class="['corner-badge', specialOfferBadgeProperties?.classesApplied]"
            :style="[specialOfferBadgeProperties?.enableBadge ? specialOfferBadgeProperties?.styles : '']"
            :aria-label="specialOfferBadgeProperties?.badgeText"
            tabindex="0"
          >
            {{ specialOfferBadgeProperties?.badgeText }}
          </div>
        </div>
        <div
          v-if="product?.detail?.bottomOverlay"
          :class="[
            'bottom-overlay-wrapper',
            { 'text-center': descriptionAlignment === 'center' },
            { 'isNotSponsoredLabel': !isSponsoredLabel }
          ]"
          tabindex="0"
        >
          <div class="bottom-overlay-label small">
            {{ product?.detail?.bottomOverlay?.text }}
          </div>
        </div>
        <div
          v-if="!isBcom && !isSponsoredLabel"
          class="corner-badge-container"
        >
          <div
            v-if="showCornerBadge"
            :class="['corner-badge', specialOfferBadgeProperties?.classesApplied]"
            :style="[specialOfferBadgeProperties?.enableBadge ? specialOfferBadgeProperties?.styles : '']"
            :aria-label="specialOfferBadgeProperties?.badgeText"
            tabindex="0"
          >
            {{ specialOfferBadgeProperties?.badgeText }}
          </div>
        </div>
      </div>
      <ProductDescription
        v-if="!isLargeCard"
        :class="[!product?.detail?.bottomOverlay && alignment === 'vertical' ? 'margin-top-xxs' : '']"
        :product="product"
        :show-more-like-this-btn="showMoreLikeThisBtn"
        :show-remove-btn="showRemoveBtn"
        :shipping-type="shippingType"
        :click-action="clickAction"
        :swatch-url-template="urlTemplate?.swatch"
        :review-link="reviewLink"
        :hide-review-text-and-rating="hideReviewTextAndRating"
        :show-review-condensed="showReviewCondensed"
        :align-pricing-block-to-right="alignPriceBlockToRight"
        :store-only="product?.storeOnly"
        :url="generatedUrl"
        :show-price-type-above-price="showPriceTypeAbovePrice"
        :wrap-product-name="wrapProductName"
        :description-alignment="descriptionAlignment"
        :related-alignment="alignment"
        :brand-class="brandClass"
        :section-spacing="sectionSpacing"
        :display-sale-price-only="displaySalePriceOnly"
        :open-in-new-tab="openInNewTab"
        :is-simplification-on="isSimplificationOn"
        :simplification-with-v1="simplificationWithV1"
        :simplification-non-bold-percentage-exp="simplificationNonBoldPercentageExp"
        :simplification-remove-sale-exp="simplificationRemoveSaleExp"
        :override-price-simplification="overridePriceSimplification"
        :suppress-tool-tip="suppressToolTip"
        :is-sortable-grid="isSortableGrid"
        :loyalty-badge="loyaltyBadge"
        :remove-tooltip-on-fgwp-badge-enabled="removeTooltipOnFgwpBadgeEnabled"
        :color-swatch-enhancement="colorSwatchEnhancement"
        :device-type="deviceType"
        @getMoreDetails="getMoreDetails"
        @setSelectedColor="updateSelectedColor"
        @moreLikeThis="moreLikeThis"
        @removeProduct="removeProduct"
        @isHovering="isHovering"
        @colorSwatch="colorSwatch"
      />
      <LargeCardDescription
        v-if="isLargeCard"
        :product="product"
        :click-action="clickAction"
        :is-large-card="isLargeCard"
        :large-card-selected-color="product?.traits?.colors?.selectedColor?.id"
        :large-card-selected-price="selectedPrice"
        :review-link="reviewLink"
        :open-in-new-tab="openInNewTab"
      />
      <ColorAndSize
        v-if="isLargeCard"
        :traits="product?.traits"
        :url-template="urlTemplate?.swatch"
        :product-url="product?.identifier?.productUrl"
        :pre-select-combination="preSelectCombination"
        :use-dropdown-for-sizes="useDropdownForSizes"
        @setSelectedColor="updateSelectedColor"
        @setSelectedSize="updateSelectedSize"
        @setSelectedPrice="updateSelectedPrice"
        @setUpcId="updateUpcId"
        @setSelectedSizeImages="updateImages"
      />
    </div>
    <div
      v-if="showChevronOnHorizontalCard && !product?.storeOnly?.inStoreOnly"
      ref="right-chevron"
      class="grid-x cell small-1 align-middle"
    >
      <span
        :class="rightChevron"
        @click="handleRightChevron"
      />
    </div>
    <div
      v-if="product?.storeOnly?.inStoreOnly"
      class="in-store-only"
    >
      <p v-html="sanitizedInStoreOnlyMessage" />
    </div>
  </div>
</template>
<script>
import { ref, computed } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { sanitize } from '@vue-util/sanitize';
import { Cookie } from '@vue-util/cookie';
import ProductThumbnailImage from './ProductThumbnail/ProductThumbnailImage';
import ProductDescription from './ProductThumbnail/ProductDescription';
import LargeCardDescription from './ProductThumbnail/LargeCardDescription';
import ColorAndSize from './ProductThumbnail/ProductDescription/ColorAndSize';
import WishListIcon from './ProductThumbnail/WishListIcon';
import isSmallCard from '../utils/isSmallCard';
import isBcom from '../utils/isBcom';
import getGeneratedUrl from '../composables/useGeneratedUrl';

export default {
  name: 'ProductThumbnail',
  components: {
    ProductThumbnailImage,
    ProductDescription,
    LargeCardDescription,
    ColorAndSize,
    WishListIcon,
  },
  props: {
    product: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
    alignment: {
      type: String,
      required: false,
      default: 'vertical',
      validator: (val) => ['vertical', 'horizontal'].includes(val),
    },
    disableSlideshow: {
      type: Boolean,
      required: false,
      default: false,
    },
    // TODO: using Picture filter to implement
    imageBackground: {
      type: String,
      required: false,
      default: undefined,
    },
    imageAspectRatio: {
      type: Number,
      required: false,
      default: isBcom ? 0.8 : 0.82,
    },
    // TODO: need implementation
    linesForProductName: {
      type: Number,
      required: false,
      default: 1,
    },
    // TODO: need add handling logical
    showMoreLikeThisBtn: {
      type: Boolean,
      required: false,
      default: false,
    },
    showWishListBtnOnImage: {
      type: Boolean,
      required: false,
      default: false,
    },
    isLargeCard: {
      type: Boolean,
      required: false,
      default: false,
    },
    imageBadge: {
      type: Object,
      required: false,
      default: () => ({
        enableBadge: false,
        badgeText: '',
      }),
    },
    preSelectUpcId: {
      type: Number,
      required: false,
      default: null,
    },
    // TODO: need implementation for PPE
    showWebId: {
      type: Boolean,
      required: false,
      default: false,
    },
    urlTemplate: {
      type: Object,
      required: false,
      default: () => (isBcom ? {
        swatch: 'https://images.bloomingdalesassets.com/is/image/BLM/swatches/',
        swatchSprite: 'https://images.bloomingdalesassets.com/is/image/BLM/',
        product: 'https://images.bloomingdalesassets.com/is/image/BLM/products/',
      } : {
        swatch: 'https://slimages.macysassets.com/is/image/MCY/swatches/',
        swatchSprite: 'https://slimages.macysassets.com/is/image/MCY/',
        product: 'https://slimages.macysassets.com/is/image/MCY/products/',
      }),
    },
    imageWidth: {
      type: Number,
      required: false,
    },
    imageSizesByBreakpoints: {
      type: Object,
      default: () => ({}),
    },
    // TODO: Add description to isolation
    shippingType: {
      type: Object,
      default: () => { },
    },
    clickAction: {
      type: String,
      default: 'linkToPdp',
      validator: (val) => ['linkToPdp', 'disabled', 'fireEvent'].includes(val),
    },
    showChevron: {
      type: Boolean,
      default: false,
      required: false,
    },
    reviewLink: {
      required: false,
      type: String,
      default: null,
    },
    hideReviewTextAndRating: {
      type: Boolean,
      required: false,
      default: false,
    },
    pricingBlockAlignment: {
      type: String,
      required: false,
      default: 'inline',
      validator: (val) => ['right', 'inline'].includes(val),
    },
    showPriceTypeAbovePrice: {
      type: Boolean,
      required: false,
      default: false,
    },
    specialOffersBadge: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    wrapProductName: {
      type: Boolean,
      required: false,
      default: false,
    },
    showRemoveBtn: {
      type: Boolean,
      required: false,
      default: false,
    },
    descriptionAlignment: {
      type: String,
      required: false,
      default: 'left',
    },
    brandClass: {
      type: String,
      required: false,
      default: () => (isBcom ? 'heavy' : 'medium'),
    },
    sectionSpacing: {
      type: String,
      required: false,
      default: 'expand',
      validator: (val) => ['collapse', 'expand'].includes(val),
    },
    displaySalePriceOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    slideshowProps: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    openInNewTab: {
      type: Boolean,
      required: false,
      default: false,
    },
    useDropdownForSizes: {
      type: Boolean,
      default: false,
    },
    skeleton: {
      type: Boolean,
      required: false,
    },
    skeletonOptions: {
      type: Object,
      default: () => ({}),
    },
    retinaAdjustment: {
      type: Boolean,
      required: false,
      default: false,
    },
    retinaQuality: {
      type: String,
      required: false,
      default: '55,0',
    },
    draggable: {
      type: String,
      default: 'auto',
      validator: (val) => ['true', 'false', 'auto'].includes(val),
    },
    fetchPriority: {
      type: Boolean,
      required: false,
      default: false,
    },
    lazyLoad: {
      type: Boolean,
      required: false,
      default: true,
    },
    selectedFacetSizes: {
      type: Array,
      default: () => ([]),
    },
    firstImagePriority: {
      type: Boolean,
      required: false,
      default: false,
    },
    isSimplificationOn: {
      type: Boolean,
      default: false,
    },
    simplificationWithV1: {
      type: Boolean,
      default: false,
    },
    simplificationNonBoldPercentageExp: {
      type: Boolean,
      default: false,
    },
    simplificationRemoveSaleExp: {
      type: Boolean,
      default: false,
    },
    overridePriceSimplification: {
      type: Boolean,
      default: false,
    },
    suppressToolTip: {
      type: Boolean,
      default: false,
    },
    isSortableGrid: {
      type: Boolean,
      default: false,
    },
    firstPictureNativeLazy: {
      type: Boolean,
      required: false,
      default: false,
    },
    loyaltyBadge: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    showReviewCondensed: {
      type: Boolean,
      required: false,
      default: false,
    },
    removeTooltipOnFgwpBadgeEnabled: {
      type: Boolean,
      default: false,
    },
    colorSwatchEnhancement: {
      type: Boolean,
      required: false,
      default: false,
    },
    deviceType: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: ['getProductDetails', 'getMoreLikeThis', 'handleRightChevron', 'updateSelectedSize', 'updateUpcId', 'removeProduct', 'wishListIconClicked', 'chevronDirection'],
  setup(props, { emit }) {
    const selectedColorId = props.product.traits?.colors?.selectedColor?.id
      || (props.product.traits?.colors?.colorMap ? props.product.traits?.colors?.colorMap[0]?.id : undefined);
    const selectedColor = ref(selectedColorId);
    const selectedSizeImages = ref([]);
    const hasSwatchlet = computed(() => props.product?.traits?.colors?.colorMap?.length > 1);
    const updateSelectedColor = useDebounceFn((colorId) => {
      selectedColor.value = colorId;
    }, 200);
    const updateSelectedSize = useDebounceFn((size) => {
      emit('updateSelectedSize', size);
    }, 200);
    const updateImages = (images) => {
      selectedSizeImages.value = images;
    };
    const getChevronDirection = (event) => {
      emit('chevronDirection', event);
    };
    const selectedPrice = ref({});
    const updateSelectedPrice = (price) => {
      selectedPrice.value = price;
    };
    const updateUpcId = useDebounceFn((upcDetails) => {
      upcDetails.upcNumber = props.product?.relationships?.upcs[upcDetails.upcId]?.identifier?.upcNumber;
      emit('updateUpcId', upcDetails);
    }, 200);
    const preSelectCombination = computed(() => {
      if (props.preSelectUpcId !== null) {
        return {
          upcSelectedColor: props.product.relationships?.upcs[props.preSelectUpcId].traits.colors?.selectedColor,
          upcSelectedSize: props.product.relationships?.upcs[props.preSelectUpcId].traits.sizes?.selectedSize,
        };
      }
      return {};
    });
    const sanitizedInStoreOnlyMessage = computed(() => {
      const options = {
        whiteList: {
          a: ['href', 'class', 'onclick'],
        },
      };
      return sanitize(props.product?.storeOnly?.message, options);
    });
    const slideshowParams = computed(() => ({
      useSlideshow: false,
      showIndicator: false,
      disableSlideshow: false,
      autoplayMode: 'manual-hover',
      autoplayInterval: 800,
      backToFirst: true,
      ...props.slideshowProps,
    }));
    const specialOfferBadgeProperties = computed(() => {
      const classesApplied = props.specialOffersBadge?.classesApplied;
      let classesAppliedTransformed = '';
      if (classesApplied) {
        if (Array.isArray(classesApplied)) {
          classesAppliedTransformed = classesApplied.join(' ');
        }
        if (typeof classesApplied === 'string') {
          classesAppliedTransformed = classesApplied;
        }
      }
      return {
        enableBadge: props.specialOffersBadge?.enableBadge && !props.showPriceTypeAbovePrice,
        badgeText: props.specialOffersBadge?.badgeText || '',
        styles: props.specialOffersBadge?.styles || [],
        classesApplied: classesAppliedTransformed,
      };
    });
    const showWishlist = computed(() => props.showWishListBtnOnImage && !props.product.detail?.flags?.masterProduct && !Cookie.isInternationalMode());

    return {
      selectedColor,
      hasSwatchlet,
      updateSelectedColor,
      updateSelectedSize,
      selectedPrice,
      updateSelectedPrice,
      updateUpcId,
      preSelectCombination,
      updateImages,
      selectedSizeImages,
      sanitizedInStoreOnlyMessage,
      showWishlist,
      slideshowParams,
      specialOfferBadgeProperties,
      getChevronDirection,
    };
  },
  data() {
    return {
      rightChevron: '',
      wishListSpacing: '',
      isHoveringValue: {},
      colorSwatchData: {},
      isBcom,
    };
  },
  computed: {
    cssImageWidth() {
      return this.alignment === 'vertical' ? '100%' : `${this.imageWidth}px`;
    },
    productThumbnailContainerClasses() {
      return {
        'product-thumbnail-container': true,
        'horizontal-alignment': this.alignment === 'horizontal',
        'vertical-alignment': this.alignment === 'vertical',
        'is-add-to-bag-card': this.pricingBlockAlignment === 'right',
        'is-large-card': this.isLargeCard,
      };
    },
    showChevronOnHorizontalCard() {
      return this.alignment === 'horizontal' && this.showChevron;
    },
    alignPriceBlockToRight() {
      return this.alignment === 'horizontal' && this.pricingBlockAlignment === 'right';
    },
    generatedUrl() {
      return getGeneratedUrl(this.product, this.selectedFacetSizes, this.colorSwatchData, this.selectedColor);
    },
    showCornerBadge() {
      return (this.specialOfferBadgeProperties.enableBadge && this.specialOfferBadgeProperties.badgeText !== '') && !this.isLargeCard;
    },
    isSponsoredLabel() {
      return this.product?.detail?.flags?.siteMonetizationProduct;
    },
  },
  mounted() {
    this.eventHandler();
  },
  beforeUnmount() {
    if (this.showChevronOnHorizontalCard) {
      this.roChevron.unobserve(document.body);
    }
    if (this.showWishlist) {
      this.roWishList.unobserve(document.body);
    }
  },
  methods: {
    getMoreDetails(event) {
      if (this.clickAction === 'fireEvent') {
        this.$emit('getProductDetails', this.product);
        event.preventDefault();
        return false;
      } if (this.clickAction === 'disabled') {
        event.preventDefault();
        return false;
      }
      return true;
    },
    moreLikeThis(productId) {
      this.$emit('getMoreLikeThis', productId);
    },
    getWishListAlignment() {
      if (isSmallCard(this.$refs.imageSize)) {
        this.wishListSpacing = 'wishlist-heart-small-card';
      } else {
        this.wishListSpacing = 'wishlist-heart-large-card';
      }
    },
    getRightChevron() {
      if (typeof window !== 'undefined') {
        if (window.innerWidth < 1024) {
          this.rightChevron = 'right-chevron-small';
        } else {
          this.rightChevron = 'right-chevron-large';
        }
      }
      return '';
    },
    eventHandler() {
      if (this.showChevronOnHorizontalCard) {
        this.roChevron = new ResizeObserver(this.getRightChevron);
        this.roChevron.observe(document.body);
      } if (this.showWishlist) {
        this.roWishList = new ResizeObserver(this.getWishListAlignment);
        this.roWishList.observe(document.body);
      }
    },
    handleRightChevron() {
      this.$emit('handleRightChevron', this.product.id);
    },
    removeProduct() {
      this.$emit('removeProduct', this.product);
    },
    wishListIconClicked(data) {
      this.$emit('wishListIconClicked', data);
    },
    isHovering(value) {
      this.isHoveringValue = value;
    },
    colorSwatch(value) {
      this.colorSwatchData = value;
    },
  },
};
</script>
<style lang="scss" scoped>
.product-thumbnail-container {
  .product-image {
    position: relative;
    width: v-bind(cssImageWidth);
  }

  .bottom-overlay-wrapper {
    margin-top: rem-calc(8);

    .bottom-overlay-label {
      color: $gray-1-color;
    }
  }

  .in-store-only {
    margin-top: rem-calc(30.5);
    width: 100%;
    max-width: rem-calc(213);
    height: rem-calc(40);
    color: $sale-color;
    font-weight: $price-reg-font-weight;

    @include breakpoint(small down) {
      margin-top: rem-calc(42);
      max-width: rem-calc(160);
      height: rem-calc(60);
    }
  }

  .clickAction {
    cursor: pointer;
  }
}

.wishlist-heart-small-card,
.wishlist-heart-large-card {
  position: absolute;
}

.wishlist-heart-small-card {
  top: rem-calc(4);
  right: rem-calc(4);
}

.wishlist-heart-large-card {
  top: rem-calc(8);
  right: rem-calc(8);
}

//style rules for product description right alignment
.horizontal-alignment {
  display: flex;
  flex-direction: row;

  .product-description {
    order: 1;
    margin-left: rem-calc(25);

    .product-name {
      margin-bottom: rem-calc(15);
    }
  }
}

.vertical-alignment.is-large-card {
  .product-description {
    order: 1;
  }

  .product-image {
    order: 2;
  }

  .color-and-size-container {
    order: 3;
  }
}

.is-add-to-bag-card {
  width: 100%;

  .product-image {
    width: rem-calc(106);
  }

  .product-description {
    width: calc(100% - rem-calc(122));
    margin-left: rem-calc(16);
  }
}
</style>
<style lang="scss" scoped brand="mcom">
@import "@csgIcons/_chevron-large-right-black.scss";
@import "@csgIcons/_chevron-medium-right-black.scss";

@include breakpoint(large) {
 .bottom-overlay-wrapper {
    &.isNotSponsoredLabel {
    background-color: $background-color;
    margin-top: 0px;
      .bottom-overlay-label {
        font-weight: $font-weight-heavy;
        text-align: center;
      }
    }
  }
}

@include breakpoint(medium down) {
 .bottom-overlay-wrapper {
    &.isNotSponsoredLabel {
      display: none
    }
  }
}

.right-chevron-large {
  @extend .chevron-large-right-black;
}

.right-chevron-small {
  @extend .chevron-medium-right-black;
}

.corner-badge-container {
  margin-bottom: rem-calc(12);
  @include breakpoint(medium down) {
    margin-bottom: rem-calc(8);
  }

  .corner-badge {
    background: $hover-color;
    padding: rem-calc(2 8);
    opacity: .95;
    font-size: rem-calc(12);
    line-height: rem-calc(18);
    pointer-events: none;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow-x: hidden;
    max-width: calc(100% - 1rem);
    margin-top: rem-calc(2);
    display: inline-block;
    vertical-align: bottom
  }
}

.corner-badge-container:not(:has(.corner-badge)) {
  margin-bottom: rem-calc(18);
  @include breakpoint(medium down) {
    margin-bottom: rem-calc(16);
  }
}

.corner-badge-container.is-sponsored-label {
  margin-bottom: rem-calc(12);
}
</style>
<style lang="scss" scoped brand="bcom">
@import "@csgIcons/_chevron-large-right.scss";
@import "@csgIcons/_chevron-small-right.scss";

.right-chevron-large {
  @extend .chevron-large-right;
}

.right-chevron-small {
  @extend .chevron-small-right;
}
</style>
